"use client";

import { useEffect } from "react";

/**
 * Patches `Node.prototype.removeChild` and `Node.prototype.insertBefore` to
 * silently handle the case where the target node is not actually a child of
 * the expected parent.
 *
 * This is a well-known workaround for React apps that run alongside browser
 * features (e.g. Chrome auto-translate, accessibility tools, extensions) which
 * can move or remove DOM nodes that React is tracking. When React's commit
 * phase later tries to reconcile those nodes, the native DOM methods throw a
 * `NotFoundError` ("The node to be removed is not a child of this node"),
 * which bubbles up to the global error boundary.
 *
 * The patches are applied once on mount and never removed because the native
 * methods must stay patched for the entire lifetime of the page.
 */
export function DomNodePatchRecovery() {
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const originalRemoveChild = Node.prototype.removeChild;

    Node.prototype.removeChild = function <T extends Node>(child: T): T {
      const childNode = child as Node | null | undefined;
      if (!childNode || childNode.parentNode !== this) {
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    };

    const originalInsertBefore = Node.prototype.insertBefore;

    Node.prototype.insertBefore = function <T extends Node>(
      newNode: T,
      referenceNode: Node | null,
    ): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        return newNode;
      }
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
  }, []);

  return null;
}
